// This is a light wrapper around the native waffle JS API.

// Keep in sync with server/basebox/waffle_flags.py
export enum WaffleFlags {
  AV_ENABLED = "av_enabled",
  STUDENT_DEMO_2025 = "student_demo_2025",
  AI_CHAT_TOOLS_ENABLED = "ai_chat_tools_enabled",
  RAG_ENABLED = "rag_enabled",
  GITBOOK_SEMANTIC_ROUTER = "gitbook_semantic_router",
  CCM_ENABLED = "allow_centralized_curriculum_management",
  DEBUG_PUBSUB = "debug_pubsub",
  DEBUG_SHAREDB = "debug_sharedb",
  DEBUG_WHITEBOARD = "debug_whiteboard",
  DEBUG_PROSEMIRROR = "debug_prosemirror",
  DEBUG_WORKSHEET_FIELD_CONTENT = "debug_worksheet_field_content",
  ENABLE_INTERCOM = "enable_intercom",
  METRICS_ENABLED = "metrics_enabled",
  MOBILE_LOGIN = "mobile_login",
  AI_DEBUGGER_VISIBLE = "ai_debugger_visible",
  AV_REDUCE_QUALITY_FOR_HIDDEN_VIDEOS = "av_reduce_quality_for_hidden_videos",
  AV_IGNORE_BROWSER_CHECKS = "av_ignore_browser_checks",
  AV_ENABLE_MOBILE_DEVICES = "av_enable_mobile_devices",
  AV_LIMIT_ACTIVE_PARTICIPANTS = "av_limit_active_participants",
  ACCESSIBILITY_UI = "accessibility_ui",
  ASSESSMENT_AI_FEEDBACK_STYLE = "assessment_ai_feedback_style",
  ASSESSMENT_AI_EVALUATION = "assessment_ai_evaluation",
}

const WAFFLE_FLAGS_LOAD_TIMEOUT_MS = 5000;
const WAFFLE_FLAGS_LOAD_POLL_INTERVAL_MS = 100;

// General //

export const areWaffleFlagsLoaded = (): boolean =>
  typeof window !== "undefined" && typeof window.waffle?.flag_is_active === "function";

export const waitForWaffleFlags = (): Promise<boolean> => {
  if (areWaffleFlagsLoaded()) {
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    const intervalId = window.setInterval(() => {
      if (areWaffleFlagsLoaded()) {
        window.clearInterval(intervalId);
        window.clearTimeout(timeoutId);
        resolve(true);
      }
    }, WAFFLE_FLAGS_LOAD_POLL_INTERVAL_MS);
    const timeoutId = window.setTimeout(() => {
      window.clearInterval(intervalId);
      resolve(areWaffleFlagsLoaded());
    }, WAFFLE_FLAGS_LOAD_TIMEOUT_MS);
  });
};

export const isWaffleFlagEnabled = (flag: string): boolean => {
  if (typeof window === "undefined" || !window?.waffle?.flag_is_active) {
    return false;
  }

  return window.waffle.flag_is_active(flag);
};

export const isWaffleSampleEnabled = (sample: string): boolean => {
  if (typeof window === "undefined" || !window.waffle?.sample_is_active) {
    return false;
  }

  return window.waffle.sample_is_active(sample);
};

export const isWaffleSwitchEnabled = (switchName: string): boolean => {
  if (typeof window === "undefined" || !window.waffle?.switch_is_active) {
    return false;
  }

  return window.waffle.switch_is_active(switchName);
};

// Specific flags //
export const isMetricsEnabled = (): boolean => isWaffleFlagEnabled(WaffleFlags.METRICS_ENABLED);
export const isMobileLoginEnabled = (): boolean => isWaffleFlagEnabled(WaffleFlags.MOBILE_LOGIN);
export const isAVEnabled = (): boolean => isWaffleFlagEnabled(WaffleFlags.AV_ENABLED);

export const isStudentDemoEnabled = (): boolean =>
  isWaffleFlagEnabled(WaffleFlags.STUDENT_DEMO_2025);

export const isAIChatToolsEnabled = (): boolean =>
  isWaffleFlagEnabled(WaffleFlags.AI_CHAT_TOOLS_ENABLED);

export const isRAGEnabled = (): boolean => isWaffleFlagEnabled(WaffleFlags.RAG_ENABLED);
export const isDebugPubsubEnabled = (): boolean => isWaffleFlagEnabled(WaffleFlags.DEBUG_PUBSUB);
export const isIntercomEnabled = (): boolean => isWaffleFlagEnabled(WaffleFlags.ENABLE_INTERCOM);
export const isCCMEnabled = (): boolean => isWaffleFlagEnabled(WaffleFlags.CCM_ENABLED);

export const isAiDebuggerVisible = (): boolean =>
  isWaffleFlagEnabled(WaffleFlags.AI_DEBUGGER_VISIBLE);

export const isReducedQualityForHiddenVideosEnabled = (): boolean =>
  isWaffleFlagEnabled(WaffleFlags.AV_REDUCE_QUALITY_FOR_HIDDEN_VIDEOS);

export const isAvIgnoreBrowserChecksEnabled = (): boolean =>
  isWaffleFlagEnabled(WaffleFlags.AV_IGNORE_BROWSER_CHECKS);

export const isAvLimitActiveParticipantsEnabled = (): boolean =>
  isWaffleFlagEnabled(WaffleFlags.AV_LIMIT_ACTIVE_PARTICIPANTS);

export const isAvMobileDevicesEnabled = (): boolean =>
  isWaffleFlagEnabled(WaffleFlags.AV_ENABLE_MOBILE_DEVICES);

export const isAccessibilityUIEnabled = (): boolean =>
  isWaffleFlagEnabled(WaffleFlags.ACCESSIBILITY_UI);

export const isAssessmentAIFeedbackStyleEnabled = (): boolean =>
  isWaffleFlagEnabled(WaffleFlags.ASSESSMENT_AI_FEEDBACK_STYLE);

export const isAssessmentAIEvaluationEnabled = (): boolean =>
  isWaffleFlagEnabled(WaffleFlags.ASSESSMENT_AI_EVALUATION);
